import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { api, setPreferredTier, PAYWALL_EVENT } from './api'
import type { Card, FrameNode, GenerateOutput, Project, PaywallInfo } from './api'
import { LoginPage } from './components/LoginPage'
import { Homepage } from './components/Homepage'
import { PaywallBanner } from './components/PaywallBanner'
import { PaywallModal } from './components/PaywallModal'
import { OnboardingOverlay } from './components/OnboardingOverlay'
import { FeedbackFab } from './components/FeedbackFab'
import { PricingPage } from './components/PricingPage'
import { SharePage } from './components/SharePage'
import { ForPm } from './components/ForPm'
import { ForDevelopers } from './components/ForDevelopers'
import { BlogIndex } from './components/BlogIndex'
import { BlogPost } from './components/BlogPost'
import { CheckoutRedirect } from './components/CheckoutRedirect'
import { NotionAi } from './components/comparisons/NotionAi'
import { ChatGpt } from './components/comparisons/ChatGpt'
import { ChatPrd } from './components/comparisons/ChatPrd'
import { LinearDocs } from './components/comparisons/LinearDocs'
import { Obsidian } from './components/comparisons/Obsidian'
import { Coda } from './components/comparisons/Coda'
import { DecisionLog } from './components/DecisionLog'
import { DecisionLogTemplate } from './components/DecisionLogTemplate'
import { ArchitectureDecisionRecord } from './components/ArchitectureDecisionRecord'
import { AiPrd } from './components/AiPrd'
import { ProductRequirementsDocument } from './components/ProductRequirementsDocument'
import { PrdTemplate } from './components/PrdTemplate'
import { ProductBriefTemplate } from './components/ProductBriefTemplate'
import { Prfaq } from './components/Prfaq'
import { ProductSpecTemplate } from './components/ProductSpecTemplate'
import { SpecDrivenDevelopment } from './components/SpecDrivenDevelopment'
import { LivingDocumentation } from './components/LivingDocumentation'
import { CompetitiveAnalysisTemplate } from './components/CompetitiveAnalysisTemplate'
import { GoToMarketStrategyTemplate } from './components/GoToMarketStrategyTemplate'
import { ProductLaunchChecklist } from './components/ProductLaunchChecklist'
import { BugReportTemplate } from './components/BugReportTemplate'
import { TechnicalDesignDocument } from './components/TechnicalDesignDocument'
import { BestAiToolsForProductManagers } from './components/BestAiToolsForProductManagers'
import { BestProductManagementSoftware } from './components/BestProductManagementSoftware'
import { BestAiNoteTakingApp } from './components/BestAiNoteTakingApp'
import { ProductRoadmapExample } from './components/ProductRoadmapExample'
import { ReleasePlanTemplate } from './components/ReleasePlanTemplate'
import { FeatureRequestTemplate } from './components/FeatureRequestTemplate'
import { WhatIsAProductRoadmap } from './components/WhatIsAProductRoadmap'
import { PrioritizationFrameworks } from './components/PrioritizationFrameworks'
import { ProductStrategy } from './components/ProductStrategy'
import { BestKnowledgeBaseSoftware } from './components/BestKnowledgeBaseSoftware'
import { ContentRoadmap } from './components/ContentRoadmap'
import { ProductDiscovery } from './components/ProductDiscovery'
import { WhatIsProductManagement } from './components/WhatIsProductManagement'
import { ReleaseNotes } from './components/ReleaseNotes'
import { UserStoryTemplate } from './components/UserStoryTemplate'
import { AcceptanceCriteria } from './components/AcceptanceCriteria'
import { SwotAnalysisTemplate } from './components/SwotAnalysisTemplate'
import { CreativeBriefTemplate } from './components/CreativeBriefTemplate'
import { ProjectProposalTemplate } from './components/ProjectProposalTemplate'
import { BusinessRoadmap } from './components/BusinessRoadmap'
import { TechnicalDocumentationTemplate } from './components/TechnicalDocumentationTemplate'
import { FromMeetingNotesToDecisions } from './components/FromMeetingNotesToDecisions'
import { ProductManagementMetrics } from './components/ProductManagementMetrics'
// Multilingual SEO landing pages (Tier-1; see outreach/seo-multilingual-plan.md)
import { JaTranscription } from './components/JaTranscription'
import { JaMeetingMinutes } from './components/JaMeetingMinutes'
import { JaSummary } from './components/JaSummary'
import { DeAudioTranskribieren } from './components/DeAudioTranskribieren'
import { DeProduktRoadmap } from './components/DeProduktRoadmap'
import { DePrd } from './components/DePrd'
import { FrPrd } from './components/FrPrd'
import { FrRetranscriptionEntretien } from './components/FrRetranscriptionEntretien'
import { ZhTwVoiceToText } from './components/ZhTwVoiceToText'
import { ZhTwMeetingMinutes } from './components/ZhTwMeetingMinutes'
import { EsTranscribirEntrevista } from './components/EsTranscribirEntrevista'
import { EsNotasReuniones } from './components/EsNotasReuniones'
import { KoVoiceToText } from './components/KoVoiceToText'
import { KoPdfSummary } from './components/KoPdfSummary'
import { ItTrascrizioneAudio } from './components/ItTrascrizioneAudio'
import { ItRiassuntoPdf } from './components/ItRiassuntoPdf'
// Multilingual SEO — batch 2 (high-volume bridges)
import { JaVideoTranscription } from './components/JaVideoTranscription'
import { JaVoiceRecorderTranscription } from './components/JaVoiceRecorderTranscription'
import { KoYoutubeSummary } from './components/KoYoutubeSummary'
import { ZhTwVideoToText } from './components/ZhTwVideoToText'
import { ZhTwCornellNotes } from './components/ZhTwCornellNotes'
import { DeVideoTranskribieren } from './components/DeVideoTranskribieren'
import { ItTrascrizioneVideo } from './components/ItTrascrizioneVideo'
import { FrTranscriptionAudio } from './components/FrTranscriptionAudio'
// Multilingual SEO — batch 3 (decision-log concept hub)
import { JaDecisionLog } from './components/JaDecisionLog'
import { ZhTwDecisionLog } from './components/ZhTwDecisionLog'
import { ZhTwDecisionLogTemplate } from './components/ZhTwDecisionLogTemplate'
import { ZhTwProductBriefTemplate } from './components/ZhTwProductBriefTemplate'
import { KoDecisionLog } from './components/KoDecisionLog'
import { EsRegistroDecisiones } from './components/EsRegistroDecisiones'
import { ItRegistroDecisioni } from './components/ItRegistroDecisioni'
import { FrJournalDecisions } from './components/FrJournalDecisions'
import { DeEntscheidungsprotokoll } from './components/DeEntscheidungsprotokoll'
// Multilingual SEO — batch 4 (Notion comparison × 7)
import { NotionAiJa } from './components/comparisons/NotionAiJa'
import { NotionAiZhTw } from './components/comparisons/NotionAiZhTw'
import { NotionAiKo } from './components/comparisons/NotionAiKo'
import { NotionAiEs } from './components/comparisons/NotionAiEs'
import { NotionAiIt } from './components/comparisons/NotionAiIt'
import { NotionAiFr } from './components/comparisons/NotionAiFr'
import { NotionAiDe } from './components/comparisons/NotionAiDe'
// Multilingual SEO — batch 5 (real-volume comparisons)
import { ObsidianKo } from './components/comparisons/ObsidianKo'
import { ObsidianJa } from './components/comparisons/ObsidianJa'
import { ConfluenceDe } from './components/comparisons/ConfluenceDe'
import { TrelloDe } from './components/comparisons/TrelloDe'
import { CodaZhTw } from './components/comparisons/CodaZhTw'
// Multilingual SEO — batch 6 (product-discovery concept hub)
import { JaProductDiscovery } from './components/JaProductDiscovery'
import { ZhTwProductDiscovery } from './components/ZhTwProductDiscovery'
import { KoProductDiscovery } from './components/KoProductDiscovery'
import { EsProductDiscovery } from './components/EsProductDiscovery'
import { ItProductDiscovery } from './components/ItProductDiscovery'
import { FrRechercheProduit } from './components/FrRechercheProduit'
import { DeProduktrecherche } from './components/DeProduktrecherche'
// Multilingual SEO — batch 7 (product-roadmap concept)
import { JaProductRoadmap } from './components/JaProductRoadmap'
import { ZhTwProductRoadmap } from './components/ZhTwProductRoadmap'
import { KoProductRoadmap } from './components/KoProductRoadmap'
import { EsRoadmapProducto } from './components/EsRoadmapProducto'
import { ItRoadmapProdotto } from './components/ItRoadmapProdotto'
import { FrRoadmapProduit } from './components/FrRoadmapProduit'
// Multilingual SEO — batch 8 (PRD concept, closes the hub)
import { JaPrd } from './components/JaPrd'
import { ZhTwPrd } from './components/ZhTwPrd'
import { KoPrd } from './components/KoPrd'
import { EsPrd } from './components/EsPrd'
import { ItPrd } from './components/ItPrd'
// Multilingual SEO — batch 9 (FR + DE real-PM extras)
import { FrDocumentationProduit } from './components/FrDocumentationProduit'
import { FrWikiEntreprise } from './components/FrWikiEntreprise'
import { FrBacklogProduit } from './components/FrBacklogProduit'
import { DeProduktmanagement } from './components/DeProduktmanagement'
import { DeProduktvision } from './components/DeProduktvision'
import { PmRoadmap } from './components/useCases/PmRoadmap'
import { DesignSystemTracking } from './components/useCases/DesignSystemTracking'
import { AnalystInvestmentMemo } from './components/useCases/AnalystInvestmentMemo'
import { Security } from './components/trust/Security'
import { Privacy } from './components/trust/Privacy'
import { DataExport } from './components/trust/DataExport'
// /zh/* — Chinese SEO variants. Distinct URLs (not runtime locale)
// because Google indexes per-URL, not per-Accept-Language.
import { NotionAiZh } from './components/comparisons/NotionAiZh'
import { ChatGptZh } from './components/comparisons/ChatGptZh'
import { ChatPrdZh } from './components/comparisons/ChatPrdZh'
import { LinearDocsZh } from './components/comparisons/LinearDocsZh'
import { ObsidianZh } from './components/comparisons/ObsidianZh'
import { CodaZh } from './components/comparisons/CodaZh'
import { DecisionLogZh } from './components/DecisionLogZh'
import { DecisionLogTemplateZh } from './components/DecisionLogTemplateZh'
import { ArchitectureDecisionRecordZh } from './components/ArchitectureDecisionRecordZh'
import { AiPrdZh } from './components/AiPrdZh'
import { ProductRequirementsDocumentZh } from './components/ProductRequirementsDocumentZh'
import { PrdTemplateZh } from './components/PrdTemplateZh'
import { ProductBriefTemplateZh } from './components/ProductBriefTemplateZh'
import { PrfaqZh } from './components/PrfaqZh'
import { ProductSpecTemplateZh } from './components/ProductSpecTemplateZh'
import { SpecDrivenDevelopmentZh } from './components/SpecDrivenDevelopmentZh'
import { LivingDocumentationZh } from './components/LivingDocumentationZh'
import { CompetitiveAnalysisTemplateZh } from './components/CompetitiveAnalysisTemplateZh'
import { GoToMarketStrategyTemplateZh } from './components/GoToMarketStrategyTemplateZh'
import { ProductLaunchChecklistZh } from './components/ProductLaunchChecklistZh'
import { BugReportTemplateZh } from './components/BugReportTemplateZh'
import { TechnicalDesignDocumentZh } from './components/TechnicalDesignDocumentZh'
import { BestAiToolsForProductManagersZh } from './components/BestAiToolsForProductManagersZh'
import { BestProductManagementSoftwareZh } from './components/BestProductManagementSoftwareZh'
import { BestAiNoteTakingAppZh } from './components/BestAiNoteTakingAppZh'
import { ProductRoadmapExampleZh } from './components/ProductRoadmapExampleZh'
import { ReleasePlanTemplateZh } from './components/ReleasePlanTemplateZh'
import { FeatureRequestTemplateZh } from './components/FeatureRequestTemplateZh'
import { WhatIsAProductRoadmapZh } from './components/WhatIsAProductRoadmapZh'
import { PrioritizationFrameworksZh } from './components/PrioritizationFrameworksZh'
import { ProductStrategyZh } from './components/ProductStrategyZh'
import { BestKnowledgeBaseSoftwareZh } from './components/BestKnowledgeBaseSoftwareZh'
import { ContentRoadmapZh } from './components/ContentRoadmapZh'
import { ProductDiscoveryZh } from './components/ProductDiscoveryZh'
import { WhatIsProductManagementZh } from './components/WhatIsProductManagementZh'
import { ReleaseNotesZh } from './components/ReleaseNotesZh'
import { UserStoryTemplateZh } from './components/UserStoryTemplateZh'
import { AcceptanceCriteriaZh } from './components/AcceptanceCriteriaZh'
import { SwotAnalysisTemplateZh } from './components/SwotAnalysisTemplateZh'
import { CreativeBriefTemplateZh } from './components/CreativeBriefTemplateZh'
import { ProjectProposalTemplateZh } from './components/ProjectProposalTemplateZh'
import { BusinessRoadmapZh } from './components/BusinessRoadmapZh'
import { TechnicalDocumentationTemplateZh } from './components/TechnicalDocumentationTemplateZh'
import { FromMeetingNotesToDecisionsZh } from './components/FromMeetingNotesToDecisionsZh'
import { ProductManagementMetricsZh } from './components/ProductManagementMetricsZh'
import { PmRoadmapZh } from './components/useCases/PmRoadmapZh'
import { DesignSystemTrackingZh } from './components/useCases/DesignSystemTrackingZh'
import { AnalystInvestmentMemoZh } from './components/useCases/AnalystInvestmentMemoZh'
import { SecurityZh } from './components/trust/SecurityZh'
import { PrivacyZh } from './components/trust/PrivacyZh'
import { DataExportZh } from './components/trust/DataExportZh'
import { Sidebar } from './components/Sidebar'
import { DiscussionPane } from './components/DiscussionPane'
import { CardsPanel } from './components/CardsPanel'
import type { FrameGapInfo } from './components/CardsPanel'
import { ColumnResizer } from './components/ColumnResizer'
import { GapLedger } from './components/GapLedger'
import { GenerateModal } from './components/GenerateModal'
import { DocumentsModal } from './components/DocumentsModal'
import type { DocTemplate } from './api'
import { ConfirmDialog } from './components/ConfirmDialog'
import { DistillModal } from './components/DistillModal'
import { ImportModal } from './components/ImportModal'
import { SettingsModal } from './components/SettingsModal'
import { AdminShell } from './components/AdminShell'
import { CardPeek } from './components/cards/CardPeek'
import { HistoryDrawer } from './components/cards/HistoryDrawer'
import { GapProbePopover } from './components/cards/GapProbePopover'
import { MobileShell } from './components/mobile/MobileShell'
import { useIsMobile } from './hooks/useIsMobile'
import type { CardProposal, DistillMode, GapProposal } from './api'
import { useCore, useCoreWireEvents } from './core'
import { useLocaleSync } from './i18n/useLocaleSync'
import * as perf from './utils/switch_perf'

interface PendingConfirm {
  title?: string
  message: string
  confirmLabel?: string
  destructive?: boolean
  onConfirm: () => void
}

// Turn / SSE / streaming orchestration + the wire helpers it used
// (reconcileCards, deriveThreadTitle, buildDiscussingGapWire,
// flattenSessionToChat, bubblesFromCompactedSnapshot) moved to the
// shared headless core/ layer — App now consumes it via useCore().

function App() {
  perf.renderMark('App');
  const { t } = useTranslation()
  // PR-26: viewport gate. Mobile-shaped viewports get the focused capture
  // companion (MobileShell) instead of the 4-pane desktop layout. Hot
  // resize across the breakpoint full-mounts/unmounts whichever shell
  // is appropriate — each maintains its own data tree (no shared state).
  const isMobile = useIsMobile()

  // Resizable column layout (desktop only). `discussionWidth` stores the
  // user's *intended* width — we clamp at render time so window shrink
  // never destroys their preference, just temporarily caps it. Cards
  // collapse is a snap-state triggered by dragging the resizer past the
  // right edge; arrow keys / clicking the tab restore.
  const [discussionWidth, setDiscussionWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return 760
    const v = window.localStorage.getItem('draftlize.layout.discussionWidth')
    const n = v ? Number(v) : NaN
    return Number.isFinite(n) ? n : 760
  })
  const [cardsCollapsed, setCardsCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem('draftlize.layout.cardsCollapsed') === '1'
  })
  const [windowW, setWindowW] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1440,
  )
  useEffect(() => {
    const h = () => setWindowW(window.innerWidth)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [])
  useEffect(() => {
    // Debounced — avoid hammering localStorage every drag frame.
    const t = setTimeout(() => {
      window.localStorage.setItem('draftlize.layout.discussionWidth', String(discussionWidth))
      window.localStorage.setItem(
        'draftlize.layout.cardsCollapsed',
        cardsCollapsed ? '1' : '0',
      )
    }, 300)
    return () => clearTimeout(t)
  }, [discussionWidth, cardsCollapsed])

  // ─── Shared orchestration via the headless core layer ───────────────
  // Auth + boot, the project SSE, the turn lifecycle, and the message /
  // card / thread / project stores all live in <CoreProvider>
  // (web/src/core). App consumes them through `useCore()` and aliases
  // them to the names the rest of this component already uses, so the
  // render + handlers below need no rename. Desktop-only view state is
  // declared fresh below.
  const core = useCore()
  const authState = core.authState
  const me = core.me
  const projects = core.projects
  const project = core.activeProject
  const threads = core.threads
  const activeThreadId = core.activeThreadId
  const cards = core.cards
  const modelChoices = core.modelChoices
  /** Active thread's bubbles — committed history + the live streaming buffer. */
  const messages = core.activeMessages
  /** Whether the active thread has a turn in flight. */
  const busy = core.busy

  // PR-15 (i18n): keep i18next's language synced to the locale chain
  // (memory N1: browser detect → users.locale → per-thread override).
  // `me.locale` is the user-wide pref; the active thread's `locale` is
  // the per-thread override, fetched on switch. Browser detection is
  // i18next's own default beneath both.
  const [activeThreadLocale, setActiveThreadLocale] = useState<string | null>(null)
  useEffect(() => {
    if (!activeThreadId) {
      setActiveThreadLocale(null)
      return
    }
    let cancelled = false
    api
      .getThreadLocale(activeThreadId)
      .then((r) => {
        if (!cancelled) setActiveThreadLocale(r.locale)
      })
      .catch(() => {
        if (!cancelled) setActiveThreadLocale(null)
      })
    return () => {
      cancelled = true
    }
  }, [activeThreadId])
  useLocaleSync(me?.locale, activeThreadLocale)
  // Bumps when SSE reports `frames_changed` for the active project. Threaded
  // into CardsPanel as `framesRevision` so its frames-fetch effect re-runs
  // on cross-tab frame mutations (rename / delete / placement override /
  // recompute) without forcing a card refetch.
  const [framesRevision, setFramesRevision] = useState(0)
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null)
  // Floating peek popover. Anchor is the originating card row's bounding
  // rect at the time of double-click (or `null` for keyboard-triggered open,
  // which centers the popover). `mode` controls whether the peek opens in
  // read-only view or directly into edit (e.g. via ⌘/Ctrl + E).
  // Cleared on close, on Esc, or when the card is no longer in the cards
  // array (e.g. it was deleted).
  const [peeked, setPeeked] = useState<
    { id: string; anchor: DOMRect | null; mode: 'view' | 'edit' } | null
  >(null)
  /** Dirty flag bubbled up from CardPeek's edit form, gating cross-card switches. */
  const [peekDirty, setPeekDirty] = useState(false)
  /** Frame MECE gap probe popover — opens when user clicks a gap chip in CardsPanel. */
  const [gapProbe, setGapProbe] = useState<{ frameId: string; node: FrameNode } | null>(null)
  /** Active frame's MECE gaps, mirrored from CardsPanel via onFrameGapsChange.
   *  The GapLedger column 4 renders these as a FRAME GAPS section. */
  const [frameGaps, setFrameGaps] = useState<FrameGapInfo[]>([])
  /** Turn ids the user is currently hover-pinning (e.g. by hovering a Gap
   *  Ledger row whose card carries `extra.context.turns`). DiscussionPane
   *  draws an amber ring around matching bubbles and scrolls the first
   *  one into view. Cleared on mouseleave. */
  const [highlightTurnIds, setHighlightTurnIds] = useState<ReadonlySet<string>>(new Set())
  /** Latched request to scroll the DiscussionPane to a specific turn. Bumped
   *  by clicking a cited turn id in GapLedger. The `nonce` lets repeated
   *  clicks on the same id re-fire the effect. */
  const [turnJumpRequest, setTurnJumpRequest] = useState<{ turnId: string; nonce: number } | null>(null)
  /** Card ids the user is currently hover-pinning by hovering ANOTHER card
   *  whose body wikilinks point at them. Mirrors the gap-row hover →
   *  discussion turn-outline pattern, just within the CardsPanel. Cleared
   *  on mouseleave. */
  const [hoverHighlightCardIds, setHoverHighlightCardIds] = useState<ReadonlySet<string>>(new Set())
  const [highlightCardId, setHighlightCardId] = useState<string | null>(null)
  // Project-switch skeleton flag. Core's IndexedDB card cache paints a
  // switch fast enough that the skeleton isn't needed — always false now.
  const switching = false
  /** Card id to render the History modal for. Triggered from broken-ref
   *  chips and the Deleted view's "view history" buttons. Null when no
   *  modal is open. Renders a HistoryDrawer overlay on top of everything. */
  const [historyModalCardId, setHistoryModalCardId] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  const [generated, setGenerated] = useState<GenerateOutput | null>(null)
  // Provenance for the in-flight generate. Captured at template-pick time so
  // when the user clicks "Save to project" we can persist the snapshot of
  // which cards (with their mtimes) fed this doc — the staleness check in
  // DocumentsModal compares those against the live cards.
  const [pendingSnapshot, setPendingSnapshot] = useState<Array<{ card_id: string; mtime: string }> | null>(null)
  const [documentsOpen, setDocumentsOpen] = useState(false)
  // Saved-document staleness banner. Server emits `documents_stale` whenever a
  // card a saved doc snapshotted has drifted; we accumulate the affected ids
  // here so the Documents button can show a small dot. Cleared when the
  // modal opens — at that point the modal itself recomputes staleness from
  // live data and is the source of truth.
  const [staleDocIds, setStaleDocIds] = useState<ReadonlySet<string>>(new Set())
  // Distill state. `distillOpen` toggles the modal; items/result/error
  // are streamed in by api.distillThread. Cleared on close. Cards and gaps
  // arrive together in the `done` event but render in two sections so the
  // user can accept independently. `null` while streaming so the modal
  // shows the progress state instead of "no items extracted".
  const [distillOpen, setDistillOpen] = useState(false)
  const [distillItems, setDistillItems] = useState(0)
  const [distillCards, setDistillCards] = useState<CardProposal[] | null>(null)
  const [distillGaps, setDistillGaps] = useState<GapProposal[] | null>(null)
  const [distillError, setDistillError] = useState<string | null>(null)
  const [distilling, setDistilling] = useState(false)
  // Mode the active distill run is using. Drives prompt selection on the
  // server and the modal's segmented control + commit-message routing
  // (audit gaps get `source=audit` injected so OPEN can chip them).
  const [distillMode, setDistillMode] = useState<DistillMode>('rescue')
  // After the user clicks Resolve on a pinned gap, surface a one-shot
  // banner ("Did this discussion surface new gaps?") with a Run Audit
  // affordance. The discussion just produced cards that resolved a gap
  // — that's exactly the moment audit is most informative. Cleared when
  // user dismisses, runs audit, or starts a new pinned discussion.
  const [postResolveAuditFor, setPostResolveAuditFor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  /** Optional action affordance shown alongside `toast` — used by the
   *  bulk-approve flow to expose an [Undo] button next to the success
   *  message. Cleared automatically when the toast clears (via the same
   *  timer that nulls `toast`, or the click-to-dismiss handler). Most
   *  callers don't touch this; they just `setToast('…')` and the action
   *  slot stays null. The dedicated `showUndoToast` helper sets both. */
  const [toastAction, setToastAction] = useState<{ label: string; onClick: () => void } | null>(null)
  /** Set toast text + an action button (e.g. "Undo") and schedule clear.
   *  Default 5s — long enough to read + click, short enough to not linger
   *  past the user's working memory of "what did I just do". */
  const showUndoToast = useCallback(
    (message: string, action: { label: string; onClick: () => void }, durationMs = 5000) => {
      setToast(message)
      setToastAction(action)
      setTimeout(() => {
        setToast(null)
        setToastAction(null)
      }, durationMs)
    },
    [],
  )
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)

  // doc/07 PR-34: paywall modal state, set on PAYWALL_EVENT (402/429).
  const [paywallInfo, setPaywallInfo] = useState<PaywallInfo | null>(null)
  useEffect(() => {
    const hp = (e: Event) => {
      const info = (e as CustomEvent<PaywallInfo>).detail
      if (info) setPaywallInfo(info)
    }
    window.addEventListener(PAYWALL_EVENT, hp)
    return () => window.removeEventListener(PAYWALL_EVENT, hp)
  }, [])
  // Which tab Settings should land on when opened. Set by the entry-point
  // (avatar → 'account', SETTINGS row → 'general', MCP chip → 'integrations').
  // Reset to undefined on close so the next open re-derives from its caller's
  // intent rather than re-using whatever tab the user last navigated to inside
  // the modal.
  const [settingsInitialSection, setSettingsInitialSection] =
    useState<import('./components/SettingsModal').SettingsSection | undefined>(undefined)
  // In-app route for the unified admin shell. Single state-machine
  // flag — no react-router needed. The sidebar link is gated to
  // is_admin; the server-side require_admin check on each admin
  // endpoint is the actual auth boundary. Mirrored to sessionStorage
  // so a refresh on the admin page lands back on admin (not main);
  // server-side require_admin still gates the data either way.
  const [view, setView] = useState<'main' | 'admin'>(() => {
    try {
      return sessionStorage.getItem('draftlize:view') === 'admin' ? 'admin' : 'main'
    } catch {
      return 'main'
    }
  })
  useEffect(() => {
    try {
      sessionStorage.setItem('draftlize:view', view)
    } catch {}
  }, [view])

  // dirty-state guard. Callback-based so we can route through the in-app
  // ConfirmDialog (no native window.confirm). If clean: action runs
  // immediately. If dirty: dialog opens; action runs only on confirm.
  // The dirty flag is owned by CardPeek's edit form (one open peek at a time)
  // and bubbled up via onDirtyChange; this guard fires on cross-card or
  // cross-project switches that would clobber unsaved edits.
  const dirtyRef = useRef(false)
  dirtyRef.current = peekDirty

  // Sidebar activity dots — busy / unread derivations live in the core
  // session registry now.
  const busyProjects = core.busyProjects
  const unreadByThread = core.unreadByThread
  const unreadProjects = core.unreadProjects

  const confirmIfDirty = useCallback((action: () => void) => {
    if (!dirtyRef.current) {
      action()
      return
    }
    setPendingConfirm({
      title: t('app.discardChangesTitle'),
      message: t('app.discardChangesMessage'),
      confirmLabel: t('app.discard'),
      destructive: true,
      onConfirm: () => {
        setPendingConfirm(null)
        action()
      },
    })
  }, [t])

  // If the peeked card disappears (deleted via SSE, etc.), close the popover.
  useEffect(() => {
    if (peeked && !cards.some((c) => c.card_id === peeked.id)) {
      setPeeked(null)
    }
  }, [cards, peeked])

  // Desktop reactions to the project SSE stream. Core already keeps the
  // card / thread / project stores fresh from these same events — this
  // layers on the desktop-only view effects: the card-highlight flash,
  // the broken-ref / stale toasts, the stale-doc badge, and the frames
  // revision that re-runs CardsPanel's frame fetch.
  useCoreWireEvents((evt) => {
    if (evt.type === 'documents_stale') {
      if (evt.documents.length > 0) {
        setStaleDocIds((prev) => {
          const next = new Set(prev)
          for (const d of evt.documents) next.add(d.id)
          return next
        })
      }
      return
    }
    if (evt.type === 'frames_changed') {
      setFramesRevision((r) => r + 1)
      return
    }
    if (evt.type === 'card_written') {
      setHighlightCardId(evt.card_id)
      setTimeout(
        () => setHighlightCardId((id) => (id === evt.card_id ? null : id)),
        2000,
      )
      if (evt.broken_wikilinks.length > 0) {
        setToast(
          t('app.cardWrittenBrokenRefs', {
            id: evt.card_id,
            refs: evt.broken_wikilinks.join(', '),
          }),
        )
        setTimeout(() => setToast(null), 4500)
      }
      return
    }
    if (evt.type === 'card_deleted') {
      setSelectedCardId((sel) => (sel === evt.card_id ? null : sel))
      if (evt.broken_links.length > 0) {
        setToast(
          t('app.cardDeletedBrokenRefs', {
            id: evt.card_id,
            refs: evt.broken_links.join(', '),
          }),
        )
        setTimeout(() => setToast(null), 4000)
      }
      return
    }
    if (evt.type === 'cards_marked_stale') {
      const n = evt.card_ids.length
      // doc/12 §3.3.2: the user's first-ever stale ripple gets the
      // teaching copy (and retires the guided-demo chip); every later
      // one gets the terse operational toast.
      let firstRipple = false
      try {
        firstRipple = localStorage.getItem('draftlize:stale_demo_done') !== '1'
        if (firstRipple) localStorage.setItem('draftlize:stale_demo_done', '1')
      } catch { /* private mode */ }
      setToast(
        firstRipple
          ? t('app.cardsMarkedStaleFirst', { id: evt.decision_id, n })
          : t('app.cardsMarkedStale', { id: evt.decision_id, n }),
      )
      setTimeout(() => setToast(null), firstRipple ? 8000 : 5000)
    }
  })

  const onNewThread = () => {
    confirmIfDirty(() => {
      void core.newThread()
    })
  }

  // Project switch is always allowed — in-flight turns in other projects
  // keep streaming. The dirty-card guard still applies because the open
  // peek belongs to the visible project and would be torn down on switch.
  const onSelectProject = (id: string) => {
    if (id === project?.id) return
    confirmIfDirty(() => {
      core.selectProject(id)
      // Fire-and-forget — boot reads this; the current session doesn't.
      api.setLastProject(id).catch(() => {})
    })
  }

  const onNewProject = (name: string, pack: string) => {
    const trimmed = name.trim()
    if (!trimmed) return
    confirmIfDirty(async () => {
      const p = await core.newProject(trimmed, pack)
      if (p) {
        api.setLastProject(p.id).catch(() => {})
        setToast(t('app.projectCreated', { name: p.name }))
        setTimeout(() => setToast(null), 1800)
      }
    })
  }

  const onImportProject = () => {
    confirmIfDirty(() => setShowImportModal(true))
  }

  // ImportModal hands back the freshly-rebuilt project — adopt it into
  // core's list and switch to it.
  const onImportSuccess = (p: Project) => {
    setShowImportModal(false)
    core.adoptProject(p)
    api.setLastProject(p.id).catch(() => {})
    setToast(t('app.projectImported', { name: p.name }))
    setTimeout(() => setToast(null), 2400)
  }

  const onOpenSettings = useCallback(
    (section?: import('./components/SettingsModal').SettingsSection) => {
      setSettingsInitialSection(section)
      setShowSettingsModal(true)
    },
    [],
  )

  // SettingsModal pack swap — optimistic patch into core's project list.
  const onPackChanged = (updated: Project) => {
    core.updateProject(updated.id, () => updated)
    setToast(t('app.packChanged', { pack: updated.pack ?? t('app.packBlank') }))
    setTimeout(() => setToast(null), 2400)
  }

  // SettingsModal project delete. The server CASCADE wiped the project;
  // its `project_changed` broadcast drops it from core's list. If the
  // deleted project was active, switch to the next available one.
  const onProjectDeleted = (deletedId: string) => {
    setShowSettingsModal(false)
    if (project?.id === deletedId) {
      const fallback = projects.find((p) => p.id !== deletedId) ?? null
      if (fallback) {
        core.selectProject(fallback.id)
        api.setLastProject(fallback.id).catch(() => {})
      } else {
        api.setLastProject(null).catch(() => {})
      }
    }
    setToast(t('app.projectDeleted'))
    setTimeout(() => setToast(null), 2400)
  }

  // Inline rename — optimistic patch into core, reconciled with the
  // server row; a failure just toasts and lets the next refetch correct.
  const onRenameProject = useCallback(
    async (id: string, name: string) => {
      core.updateProject(id, (p) => ({ ...p, name }))
      try {
        const updated = await api.renameProject(id, name)
        core.updateProject(id, () => updated)
      } catch (e) {
        setError(t('app.renameProjectFailed', { error: (e as Error).message }))
      }
    },
    [core, t],
  )

  const onRenameThread = useCallback(
    (id: string, title: string) => {
      void core.renameThread(id, title)
    },
    [core],
  )

  const onSelectThread = (id: string) => {
    confirmIfDirty(() => {
      core.selectThread(id)
    })
  }

  const onArchiveThread = (id: string) => {
    const thread = threads.find((t) => t.id === id)
    setPendingConfirm({
      title: t('app.archiveThreadTitle'),
      message: t('app.archiveThreadMessage', {
        title: thread?.title || t('app.untitled'),
      }),
      confirmLabel: t('app.archive'),
      destructive: true,
      onConfirm: async () => {
        setPendingConfirm(null)
        // Don't pull the rug out from a turn still streaming into this
        // thread — the server task keeps writing even after archive.
        if (core.busyFor(id)) {
          setError(t('app.archiveThreadMidTurn'))
          setTimeout(() => setError(null), 2200)
          return
        }
        try {
          await core.archiveThread(id)
          // archiveThread drops the row from the list; if it was the
          // active thread, clear the selection so the discussion empties.
          if (activeThreadId === id) {
            core.selectThread(null)
          }
          setToast(t('app.threadArchived'))
          setTimeout(() => setToast(null), 1800)
        } catch (e) {
          setError(t('app.archiveThreadFailed', { error: (e as Error).message }))
        }
      },
    })
  }

  // Keyboard shortcuts (no modifier, gated on "not currently typing"):
  //   e   — open peek in edit mode for the selected card
  //   n   — new thread
  //   Esc — close topmost transient surface in priority order
  // ⌘↵ — send chat (handled in DiscussionPane textarea).
  // Modifier-key shortcuts are deliberately avoided; ⌘N collides with Chrome.
  //
  // Note: when a peek is open, CardPeek installs its own capture-phase Esc
  // handler that beats this one — that's intentional so the peek can route
  // close through its own dirty-guard logic before App's chain runs.
  useEffect(() => {
    const isTyping = (el: EventTarget | null) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const handler = (e: KeyboardEvent) => {
      if (pendingConfirm) return;

      // ⌘D / Ctrl+D — open distill (preempts the browser's bookmark dialog).
      if ((e.metaKey || e.ctrlKey) && (e.key === 'd' || e.key === 'D')) {
        if (activeThreadId && messages.length > 0 && !busy && !distilling) {
          e.preventDefault();
          void onDistill();
          return;
        }
      }
      // Esc — close topmost transient surface in priority order.
      if (e.key === 'Escape') {
        if (distillOpen) {
          setDistillOpen(false);
          e.preventDefault();
          return;
        }
        if (generated) {
          setGenerated(null);
          setPendingSnapshot(null);
          e.preventDefault();
          return;
        }
        if (documentsOpen) {
          setDocumentsOpen(false);
          e.preventDefault();
          return;
        }
        if (gapProbe) {
          setGapProbe(null);
          e.preventDefault();
          return;
        }
        if (selectedCardId) {
          setSelectedCardId(null);
          e.preventDefault();
          return;
        }
        return;
      }
      // Combo letter shortcuts — guarded against input fields so they never
      // steal focus from in-progress typing.
      if (isTyping(e.target)) return;
      // ⌘/Ctrl + E — open selected card's peek directly in edit mode. If the
      // same card is already peeked we still flip it to edit (re-mounting the
      // peek with mode='edit' resets the form to current card values).
      if ((e.metaKey || e.ctrlKey) && (e.key === 'e' || e.key === 'E')) {
        if (selectedCardId) {
          setPeeked({ id: selectedCardId, anchor: null, mode: 'edit' });
          e.preventDefault();
        }
        return;
      }
      // ⌘/Ctrl + I — create a new thread.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'i' || e.key === 'I')) {
        if (project) {
          onNewThread();
          e.preventDefault();
        }
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generated, selectedCardId, project, pendingConfirm, activeThreadId, messages.length, busy, distilling, distillOpen, gapProbe]);

  // Single click = select only (highlights the card row, no panel change).
  // Double click = open peek popover (view mode). ⌘/Ctrl + E opens peek in
  // edit mode for the currently-selected card.
  const onSelectCard = (id: string) => {
    setSelectedCardId(id === selectedCardId ? null : id)
  }

  const onPeekCard = (id: string, anchor: DOMRect) => {
    // Selecting too gives the row the same highlight treatment as a normal
    // single click, so the user knows which card the peek belongs to.
    setSelectedCardId(id)
    setPeeked({ id, anchor, mode: 'view' })
  }

  // Click-to-jump from a chat tool-call row (e.g. propose_card). Selects
  // the card (CardRow's effect scrolls the row into view), flashes it for
  // ~2s so the user can find it after the peek closes, and opens the peek
  // popover anchored to the originating chat row's rect (CardPeek falls
  // back to centered when there isn't room to its left).
  const onJumpToCard = (id: string, anchor: DOMRect) => {
    setSelectedCardId(id)
    setHighlightCardId(id)
    setTimeout(
      () => setHighlightCardId((prev) => (prev === id ? null : prev)),
      2000,
    )
    setPeeked({ id, anchor, mode: 'view' })
  }

  const onClosePeek = () => {
    confirmIfDirty(() => {
      setPeeked(null)
      setPeekDirty(false)
    })
  }

  const onGapClick = (_frameId: string, node: FrameNode) => {
    confirmIfDirty(() => {
      setGapProbe({ frameId: _frameId, node })
    })
  }

  /**
   * Persist the peek's edit form. Resolves once the patched card has replaced
   * its row in `cards`; the peek itself remains open in edit mode so the user
   * can verify what landed (the form repopulates from the fresh card via the
   * card-identity effect).
   */
  const onSavePeek = async (
    patch: { kind: string; title: string; body: string; pack: string },
  ) => {
    if (!project || !peeked) return
    try {
      const updated = await api.patchCard(project.id, peeked.id, patch)
      core.patchCards((prev) =>
        prev.map((c) => (c.card_id === updated.card_id ? updated : c)),
      )
      setPeekDirty(false)
      setToast(t('app.cardSaved', { id: updated.card_id }))
      setTimeout(() => setToast(null), 1800)
    } catch (e) {
      setError(t('app.saveFailed', { error: (e as Error).message }))
      throw e
    }
  }

  const onDeletePeek = async (reason?: string) => {
    if (!project || !peeked) return
    try {
      const out = await api.deleteCard(project.id, peeked.id, reason)
      core.patchCards((prev) => prev.filter((c) => c.card_id !== out.card_id))
      setSelectedCardId(null)
      setPeeked(null)
      setPeekDirty(false)
      const msg = out.broken_links.length > 0
        ? t('app.cardDeletedBrokenReferences', {
            id: out.card_id,
            refs: out.broken_links.join(', '),
          })
        : t('app.cardDeleted', { id: out.card_id })
      setToast(msg)
      setTimeout(() => setToast(null), 4500)
    } catch (e) {
      setError(t('app.deleteFailed', { error: (e as Error).message }))
      throw e
    }
  }

  /**
   * PR-41: flip the peeked card's implementation_status. Local state
   * updates straight through `setCards` so the badge re-renders without
   * a roundtrip — server returns the freshly-written value which we
   * patch into the row. No SSE wiring on this column today; FE is the
   * only writer (CLI/MCP also write but we don't subscribe to that
   * channel in this page).
   */
  const onSetImplementationStatus = async (
    status: import('./api').ImplementationStatus | null,
  ) => {
    if (!project || !peeked) return
    try {
      const res = await api.setCardImplementationStatus(project.id, peeked.id, status)
      core.patchCards((prev) =>
        prev.map((c) =>
          c.card_id === res.id
            ? { ...c, implementation_status: res.implementation_status }
            : c,
        ),
      )
      setToast(
        res.implementation_status
          ? t('app.cardMarkedStatus', {
              id: res.id,
              status: res.implementation_status,
            })
          : t('app.cardStatusCleared', { id: res.id }),
      )
      setTimeout(() => setToast(null), 1800)
    } catch (e) {
      setError(t('app.markStatusFailed', { error: (e as Error).message }))
      throw e
    }
  }

  /**
   * Approve an interpretation_needed gap proposal — promote it to its
   * proposed_kind, preserving the same id. Server reads `extra.proposed_kind`
   * when `to_kind` is omitted; here we pass an empty body so the gap's
   * existing title/body land verbatim on the promoted card.
   */
  const onApproveProposal = async (cardId: string) => {
    if (!project) return
    try {
      const promoted = await api.promoteCard(project.id, cardId, {})
      core.patchCards((prev) =>
        prev.map((c) => (c.card_id === promoted.card_id ? promoted : c)),
      )
      // Approving a pinned gap consumes it — clear the pin (in every thread
      // that pinned it) so the agent doesn't keep getting a [discussing-gap
      // c-X] hint about a card that's no longer a gap.
      core.unpinGapEverywhere(cardId)
      setToast(t('app.proposalApproved', {
        id: promoted.card_id,
        kind: promoted.kind,
      }))
      setTimeout(() => setToast(null), 2200)
    } catch (e) {
      setError(t('app.approveFailed', { error: (e as Error).message }))
      throw e
    }
  }

  /**
   * Edit-and-approve an interpretation_needed proposal in one round-trip.
   * Wired into CardPeek's "Save & Approve" button: applies title/body
   * edits and flips kind to `proposed_kind` server-side, all in one POST.
   * Closes the peek on success since the card is now a regular kind.
   */
  const onSaveAndApprovePeek = async (
    patch: { title: string; body: string },
  ) => {
    if (!project || !peeked) return
    try {
      const promoted = await api.promoteCard(project.id, peeked.id, patch)
      core.patchCards((prev) =>
        prev.map((c) => (c.card_id === promoted.card_id ? promoted : c)),
      )
      core.unpinGapEverywhere(peeked.id)
      setPeekDirty(false)
      setPeeked(null)
      setToast(t('app.proposalApproved', {
        id: promoted.card_id,
        kind: promoted.kind,
      }))
      setTimeout(() => setToast(null), 2200)
    } catch (e) {
      setError(t('app.approveFailed', { error: (e as Error).message }))
      throw e
    }
  }

  const onRejectProposal = async (cardId: string) => {
    if (!project) return
    try {
      const out = await api.rejectGap(project.id, cardId)
      core.patchCards((prev) => prev.filter((c) => c.card_id !== out.card_id))
      // The reject endpoint also patches `extra.dismissed_gaps` on every
      // blocks target — refresh those so the agent's next round-trip sees
      // the latest frontmatter. Cheap: a single listCards re-pull.
      if (out.blocks_marked.length > 0) {
        core.refreshCards()
      }
      core.unpinGapEverywhere(cardId)
      const note =
        out.blocks_marked.length > 0
          ? t('app.proposalRejectedMarked', {
              id: out.card_id,
              n: out.blocks_marked.length,
            })
          : t('app.proposalRejected', { id: out.card_id })
      setToast(note)
      setTimeout(() => setToast(null), 2200)
    } catch (e) {
      setError(t('app.rejectFailed', { error: (e as Error).message }))
      throw e
    }
  }

  const runGenerate = async (template: DocTemplate) => {
    if (!project || generating) return
    setGenerating(true)
    setError(null)
    // Capture the source snapshot AT GENERATE TIME — the saved doc's
    // staleness later is judged against this exact set, not the cards as
    // they were when the user finally clicks Save.
    setPendingSnapshot(cards.map((c) => ({ card_id: c.card_id, mtime: c.mtime })))
    setDocumentsOpen(false) // close picker so the streaming modal is the focus
    setGenerated({ markdown: '', template, card_count: cards.length })
    try {
      await api.generate(project.id, template, (evt) => {
        if (evt.type === 'started') {
          setGenerated((prev) =>
            prev ? { ...prev, card_count: evt.card_count } : prev,
          )
        } else if (evt.type === 'delta') {
          setGenerated((prev) =>
            prev ? { ...prev, markdown: prev.markdown + evt.text } : prev,
          )
        } else if (evt.type === 'done') {
          setGenerated({
            markdown: evt.markdown,
            template: evt.template,
            card_count: evt.card_count,
          })
          core.refreshWallet() // 计费：生成文档扣费后刷新余额
        }
      })
    } catch (e) {
      setError(t('app.generateFailed', { error: (e as Error).message }))
      setGenerated(null)
      setPendingSnapshot(null)
    } finally {
      setGenerating(false)
    }
  }

  /** "Documents" footer button → open browser modal. From there the user
   *  picks a template (calls runGenerate) or selects a saved doc to view.
   *  Opening clears the badge — at that point the modal itself recomputes
   *  staleness against live data, so the cached set has served its purpose. */
  const onOpenDocuments = () => {
    if (!project) return
    setDocumentsOpen(true)
    setStaleDocIds(new Set())
  }

  /** Save the just-generated doc with the user-chosen title from the
   *  GenerateModal's title input. */
  const onSaveGenerated = async (title: string) => {
    if (!project || !generated || !pendingSnapshot) return
    await api.saveDocument(project.id, {
      title,
      template: generated.template,
      markdown: generated.markdown,
      source_snapshot: pendingSnapshot,
    })
    // Re-open Documents so the user lands back on the list with the new
    // entry visible. Closing GenerateModal happens via its own ✕.
    setDocumentsOpen(true)
  }

  // Distill flow. Open the modal eagerly so the streaming progress is
  // visible, then either reveal proposals or surface an error. Closing the
  // modal aborts visibly but the in-flight HTTP request continues — that's
  // intentional, the next click reopens to the same result.
  //
  // `mode` defaults to `rescue` (current ⌘D behavior). The OPEN column's
  // "Audit Gaps" button passes `audit`; the modal's segmented control can
  // re-run with a different mode without closing.
  //
  // `targetMessageIndex` (optional) narrows the transcript to a single
  // assistant message — used by the inline "Convert to gaps" button on
  // a chat bubble to decompose just that artifact, not the whole thread.
  const onDistill = async (
    mode: DistillMode = 'rescue',
    targetMessageIndex?: number,
  ) => {
    if (!activeThreadId || distilling || busy) return
    setDistilling(true)
    setDistillOpen(true)
    setDistillItems(0)
    setDistillCards(null)
    setDistillGaps(null)
    setDistillError(null)
    setDistillMode(mode)
    try {
      await api.distillThread(activeThreadId, (evt) => {
        if (evt.type === 'tick') setDistillItems(evt.items)
        else if (evt.type === 'done') {
          setDistillCards(evt.cards)
          setDistillGaps(evt.gaps)
          core.refreshWallet() // 计费：distill 扣费后刷新余额
        }
        else if (evt.type === 'error') setDistillError(evt.message)
      }, mode, targetMessageIndex)
    } catch (e) {
      setDistillError((e as Error).message)
    } finally {
      setDistilling(false)
    }
  }

  // Inline "Convert to gaps" wiring. The DiscussionPane hands us a turnId
  // (e.g. "t-42") for an assistant message that looks like a structured
  // artifact. Our session uses turn ids of the form t-N where N is the
  // 0-based index in session.messages, so we can parse to get the index
  // the backend wants. Falls back silently if parse fails (button still
  // hidden in that case via the turnId-required check upstream).
  const onConvertToGaps = (turnId: string) => {
    const m = /^t-(\d+)$/.exec(turnId)
    if (!m) return
    const idx = Number(m[1])
    if (!Number.isFinite(idx) || idx < 0) return
    void onDistill('decompose', idx)
  }

  // User accepted some cards and/or gaps from the distill modal. We hand
  // both lists to the agent as a single structured request — write_card
  // for cards (transcribing what was already said), propose_card for gaps
  // (so they land in the Gap Ledger for Approve / Discuss / Reject). Going
  // through the agent instead of raw POSTs lets the writes flow through
  // the SSE/cards path and shows up in thread history; it also gives the
  // agent a chance to spot conflicts with existing cards before writing.
  const onDistillAccept = (chosen: { cards: CardProposal[]; gaps: GapProposal[] }) => {
    setDistillOpen(false)
    if (chosen.cards.length === 0 && chosen.gaps.length === 0) return

    const blocks: string[] = []
    if (chosen.cards.length > 0) {
      const lines = chosen.cards.map(
        (p, i) =>
          `${i + 1}. (${p.kind}) ${p.title}\n   ${p.body.replace(/\n/g, ' ')}`,
      )
      blocks.push(
        `## Cards to write (use write_card)\n` +
          `These are crystallised — I confirmed them. Write each as the kind shown.\n\n` +
          lines.join('\n'),
      )
    }
    if (chosen.gaps.length > 0) {
      const lines = chosen.gaps.map((g, i) => {
        const blocksStr = g.blocks && g.blocks.length > 0
          ? ` blocks=[${g.blocks.join(', ')}]`
          : ''
        const priorityStr = g.priority ? ` priority=${g.priority}` : ''
        return (
          `${i + 1}. (proposed_kind=${g.proposed_kind})${blocksStr}${priorityStr}\n` +
          `   title: ${g.title}\n` +
          `   body: ${g.body.replace(/\n/g, ' ')}\n` +
          `   ambiguity_reason: ${g.ambiguity_reason}`
        )
      })
      // Source = which distill mode produced these gaps. Surfaced on the
      // gap card as extra.source so OPEN can render a chip ("audit", etc.)
      // and group/sort accordingly. Priority (if present) goes into extra
      // too so OPEN can group by p0/p1/p2/p3.
      const extraInstr =
        `For each gap, call propose_card with proposed_kind / title / body / ` +
        `ambiguity_reason / blocks. Also pass extra={ source: "${distillMode}"` +
        (chosen.gaps.some((g) => g.priority)
          ? `, priority: "<the priority shown above>"`
          : '') +
        ` } so the gap is tagged in OPEN.`
      blocks.push(
        `## Gaps to propose (use propose_card)\n` +
          `These are open items I want surfaced in the Gap Ledger. ` +
          extraInstr +
          `\n\n` +
          lines.join('\n'),
      )
    }

    const total = chosen.cards.length + chosen.gaps.length
    const intro =
      `Please commit the following ${total} item${total === 1 ? '' : 's'} ` +
      `I just confirmed from our distill review. Do NOT ask follow-up questions; ` +
      `just write/propose them as instructed below.`

    void onSend(`${intro}\n\n${blocks.join('\n\n')}`)
  }

  // Send a chat turn. The turn engine in core owns the whole lifecycle —
  // thread creation, queue-on-busy + drain, the SSE stream + streaming
  // buffer, auto-rename, the cross-thread/project bookkeeping, and (when
  // a gap is pinned to the thread) the discussing-gap wire wrapping. App
  // just forwards the user's text and any image refs.
  const onSend = (
    text: string,
    options?: { imageAttachments?: string[] },
  ) => {
    setError(null)
    void core.sendMessage(text, options?.imageAttachments)
  }

  // Resolve peeked card from the current cards list. If it's gone (deleted
  // via SSE, etc.), don't render the popover — the peek state will be
  // cleared on the next render via the effect below.
  const peekedCard = peeked ? cards.find((c) => c.card_id === peeked.id) ?? null : null
  const knownCardIds = useMemo(
    () => new Set(cards.map((c) => c.card_id)),
    [cards],
  )

  // Pin for the active thread — core tracks the per-thread pinned gap.
  const pinnedGapId = core.pinnedGap
  // Resolve the pinned card. Two kinds count as "discussable" pins:
  //   - kind=gap: agent proposal awaiting Approve/Discuss/Reject (legacy use)
  //   - kind=question: an open question the user is answering (post-redesign:
  //     question lives in OPEN, the [answer ↗] button pins it here)
  // If the card is gone OR has been resolved away (gap → promoted to its
  // proposed_kind, question → archived), unpin so we don't keep injecting a
  // stale [discussing-gap] hint into outgoing messages.
  const pinnedGap = useMemo(() => {
    if (!pinnedGapId) return null
    const c = cards.find((c) => c.card_id === pinnedGapId)
    if (!c) return null
    if (c.kind !== 'gap' && c.kind !== 'question') return null
    if (c.archived) return null
    return c
  }, [pinnedGapId, cards])
  useEffect(() => {
    if (pinnedGapId && !pinnedGap) core.unpinGapEverywhere(pinnedGapId)
  }, [pinnedGapId, pinnedGap, core])

  // Extracted so GapLedger (gap + question rows), DiscussionPane (inline
  // propose_card badges), and Mobile route Discuss through the same path:
  // dedup against an existing thread that already pins this card, otherwise
  // spawn a new thread + opener.
  //
  // Accepts both kind=gap (agent proposal) and kind=question (open question
  // the user wants to answer). For gaps we auto-fire `openerForGap` so the
  // agent immediately sees the proposal context; for questions we let the
  // user write the first turn (the question title alone isn't usually enough
  // for the agent to start drafting an answer — they want to add framing).
  const onDiscussProposal = (cardId: string) => {
    if (!project) return
    // Already a thread pinned to this card? Switch to it instead of
    // spawning a duplicate.
    const existingTid = Object.entries(core.pinnedGapByThread)
      .find(([, gid]) => gid === cardId)?.[0]
    if (existingTid && threads.some((t) => t.id === existingTid)) {
      confirmIfDirty(() => {
        core.selectThread(existingTid)
      })
      return
    }
    confirmIfDirty(async () => {
      const card = cards.find((c) => c.card_id === cardId)
      const seed =
        card?.title?.trim() ||
        card?.body.split('\n').find((l) => l.trim().length > 0)?.trim() ||
        cardId
      const isQuestion = card?.kind === 'question'
      const titlePrefix = isQuestion
        ? t('app.threadTitleAnswer')
        : t('app.threadTitleDiscuss')
      const title = `${titlePrefix} · ${seed}`.slice(0, 70)
      const thread = await core.newThread(title)
      if (!thread) return
      core.pinGap(cardId)
      // For a gap, fire the synthetic opener so the agent immediately sees
      // the proposal context (proposed_kind / ambiguity_reason / blocks).
      // A question carries no proposal shape — the user writes the first
      // turn themselves; the pin alone wraps it as a discussing-gap turn.
      if (card?.kind === 'gap') {
        void core.discussGap(cardId)
      }
    })
  }

  /**
   * Soft-archive a question. The question card stays in the DB so wikilinks
   * pointing at it still resolve, but it disappears from the OPEN QUESTIONS
   * section. Used by the [archive] button on each OPEN QUESTIONS row when
   * the user judges the question answered (or no longer relevant).
   *
   * Equivalent to the user toggling `archived=true` on a regular card via
   * the existing PATCH /archived endpoint — no new server work.
   */
  const onArchiveQuestion = async (cardId: string) => {
    if (!project) return
    try {
      const updated = await api.setCardArchived(project.id, cardId, true)
      core.patchCards((prev) =>
        prev.map((c) => (c.card_id === updated.card_id ? updated : c)),
      )
      // If this question was pinned to a thread, drop the pin — the user
      // has signalled they're done with it.
      core.unpinGapEverywhere(cardId)
      setToast(t('app.cardArchived', { id: updated.card_id }))
      setTimeout(() => setToast(null), 1800)
    } catch (e) {
      setError(t('app.archiveFailed', { error: (e as Error).message }))
    }
  }

  /**
   * Bulk-approve a batch of agent proposals from a single chat bubble.
   * Promotes each in parallel (server-side promote is idempotent and
   * preserves the id), then surfaces an [Undo] toast for 5 seconds.
   *
   * Undo path: since `/reject` only accepts `kind=gap` and these cards are
   * no longer gaps post-promote, we undo by `delete_card` on each promoted
   * id. The proposals themselves are gone — if the user wanted them back
   * they can ask the agent to re-propose. The undo's purpose is "I clicked
   * too fast", not full state restoration.
   *
   * Returns `{ ok, failed }` so the caller (DiscussionPane bulk button) can
   * collapse / mark its row state correctly even if some promotes failed.
   */
  const onApproveProposalBatch = async (cardIds: string[]) => {
    if (!project || cardIds.length === 0) return { ok: [] as string[], failed: [] as string[] }
    const pid = project.id
    const results = await Promise.allSettled(
      cardIds.map((cid) => api.promoteCard(pid, cid, {})),
    )
    const ok: string[] = []
    const failed: string[] = []
    const promotedCards: Card[] = []
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        ok.push(cardIds[i])
        promotedCards.push(r.value)
      } else {
        failed.push(cardIds[i])
      }
    })
    if (promotedCards.length > 0) {
      core.patchCards((prev) => {
        const byId = new Map(prev.map((c) => [c.card_id, c]))
        for (const fresh of promotedCards) byId.set(fresh.card_id, fresh)
        return Array.from(byId.values())
      })
      // Clear any thread pins that referenced just-promoted gaps (same as
      // single-shot onApproveProposal does).
      for (const c of promotedCards) core.unpinGapEverywhere(c.card_id)
    }
    if (failed.length > 0) {
      setError(t('app.bulkApproveFailed', {
        failed: failed.length,
        total: cardIds.length,
      }))
    }
    if (promotedCards.length > 0) {
      // Compose a destination breakdown: how many went to OPEN (kind=question)
      // vs cards (everything else). The toast names both so the user can
      // verify the split before deciding to undo.
      const toOpen = promotedCards.filter((c) => c.kind === 'question').length
      const toCards = promotedCards.length - toOpen
      const parts: string[] = []
      if (toCards > 0) parts.push(t('app.bulkApproveToCards', { n: toCards }))
      if (toOpen > 0) parts.push(t('app.bulkApproveToOpen', { n: toOpen }))
      const msg = t('app.bulkApproveAccepted', {
        n: promotedCards.length,
        parts: parts.join(' · '),
      })
      const ids = promotedCards.map((c) => c.card_id)
      showUndoToast(msg, {
        label: t('app.undo'),
        onClick: async () => {
          // Parallel delete — on failure we just log; the user already saw
          // confirmation, so the undo is best-effort and shouldn't open a
          // confirm dialog or block on individual failures.
          const undoResults = await Promise.allSettled(
            ids.map((cid) => api.deleteCard(pid, cid)),
          )
          core.patchCards((prev) => prev.filter((c) => !ids.includes(c.card_id)))
          const undoneFails = undoResults.filter((r) => r.status === 'rejected').length
          if (undoneFails > 0) {
            setError(t('app.undoDeleteFailed', {
              failed: undoneFails,
              total: ids.length,
            }))
          } else {
            setToast(t('app.cardsUndone', { n: ids.length }))
            setTimeout(() => setToast(null), 1800)
          }
        },
      })
    }
    return { ok, failed }
  }

  // doc/07 PR-31: post-login return-URL handoff. LoginPage stashed
  // a `return` path in localStorage when the user was bounced here
  // from a marketing CTA (typically `/checkout?tier=pro`). When auth
  // flips to 'in', replay it once — same-origin only, key is wiped
  // after read so we don't loop on subsequent re-mounts.
  useEffect(() => {
    if (authState !== 'in') return
    if (typeof window === 'undefined') return
    let ret: string | null = null
    try {
      ret = window.localStorage.getItem('draftlize.post_login_return')
      if (ret) window.localStorage.removeItem('draftlize.post_login_return')
    } catch {
      return
    }
    if (ret && ret.startsWith('/') && !ret.startsWith('//')) {
      // location.replace so back-button doesn't bounce to / and re-trigger.
      window.location.replace(ret)
    }
  }, [authState])

  // PR-10: gate the entire app behind the cookie session.
  // - 'loading' → minimal splash so the user sees we're alive while
  //   /api/me resolves (typically <100ms in dev).
  // - 'out'     → render <LoginPage />, which links to the GitHub OAuth
  //   start endpoint. After login the OAuth callback 302s back to /,
  //   App re-mounts, and we land in 'in'.
  // - 'in'      → existing app shell.
  if (authState === 'loading') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--paper-0)',
          color: 'var(--ink-3)',
          fontSize: 12,
          letterSpacing: '.08em',
        }}
        className="mono"
      >
        {t('common.loading')}
      </div>
    )
  }
  // Public marketing routes — accessible whether authed or not. Each
  // page sets its own <title> + meta description via MarketingShell so
  // search crawlers index them as distinct surfaces. We check these
  // BEFORE the auth gate so authed users clicking "Pricing" in the
  // marketing nav still see the public pricing page rather than getting
  // bounced to the app shell. Fly+axum already serve index.html for
  // unknown paths (SPA fallback), so /pricing, /for-pm, /for-developers
  // all hit App.tsx with the right pathname.
  //
  // Trailing slash normalize: tower-http's ServeDir 307-redirects
  // /pricing → /pricing/ so it can serve dist/pricing/index.html. The
  // browser URL ends up with the trailing slash, so all our exact-match
  // checks below need to see a trailing-slash-stripped path. "/" itself
  // stays "/" — the replace would leave "" which we OR back to "/".
  const _path =
    (typeof window !== 'undefined' ? window.location.pathname : '/').replace(
      /\/+$/,
      '',
    ) || '/'
  // doc/12 §3.2.1: public share pages — auth-independent, render the
  // same for logged-in and anonymous visitors (it's the propagation
  // surface; a viewer may well be both).
  if (_path.startsWith('/s/')) {
    const shareId = _path.slice('/s/'.length).replace(/\/+$/, '')
    if (shareId) return <SharePage shareId={shareId} />
  }
  if (_path === '/pricing') return <PricingPage />
  if (_path === '/for-pm') return <ForPm />
  if (_path === '/for-developers') return <ForDevelopers />
  // /zh/* counterparts for the 3 core marketing funnels — same
  // components, i18n detector flips locale via URL prefix so initial
  // render is in zh. MarketingShell internally reads the path prefix
  // and emits hreflang/canonical for the zh variant.
  if (_path === '/zh/pricing') return <PricingPage />
  if (_path === '/zh/for-pm') return <ForPm />
  if (_path === '/zh/for-developers') return <ForDevelopers />
  if (_path === '/blog') return <BlogIndex />
  if (_path.startsWith('/blog/')) {
    // Slug = everything after /blog/, trimmed of trailing slash.
    const slug = _path.slice('/blog/'.length).replace(/\/+$/, '')
    return <BlogPost slug={slug} />
  }
  // zh blog variants — same components, locale='zh' (translation when
  // present, English fallback otherwise).
  if (_path === '/zh/blog') return <BlogIndex locale="zh" />
  if (_path.startsWith('/zh/blog/')) {
    const slug = _path.slice('/zh/blog/'.length).replace(/\/+$/, '')
    return <BlogPost slug={slug} locale="zh" />
  }
  // Comparison pages — SEO long-tail for "X vs Y" keyword clusters.
  if (_path === '/comparisons/notion-ai') return <NotionAi />
  if (_path === '/comparisons/chatgpt') return <ChatGpt />
  if (_path === '/comparisons/chatprd') return <ChatPrd />
  if (_path === '/comparisons/linear-docs') return <LinearDocs />
  if (_path === '/comparisons/coda') return <Coda />
  if (_path === '/comparisons/obsidian') return <Obsidian />
  // Use case pages — concrete persona × scenario walk-throughs.
  if (_path === '/use-cases/pm-roadmap') return <PmRoadmap />
  if (_path === '/use-cases/design-system-tracking') return <DesignSystemTracking />
  if (_path === '/use-cases/analyst-investment-memo') return <AnalystInvestmentMemo />
  // Trust pages — B2B / procurement / privacy-conscious signals.
  if (_path === '/security') return <Security />
  if (_path === '/privacy') return <Privacy />
  if (_path === '/data-export') return <DataExport />
  // Resource / guide landing pages — SEO clusters (decision log, PRD, etc.)
  if (_path === '/decision-log') return <DecisionLog />
  if (_path === '/decision-log-template') return <DecisionLogTemplate />
  if (_path === '/architecture-decision-record') return <ArchitectureDecisionRecord />
  if (_path === '/ai-prd') return <AiPrd />
  if (_path === '/product-requirements-document') return <ProductRequirementsDocument />
  if (_path === '/prd-template') return <PrdTemplate />
  if (_path === '/product-brief-template') return <ProductBriefTemplate />
  if (_path === '/prfaq') return <Prfaq />
  if (_path === '/product-spec-template') return <ProductSpecTemplate />
  if (_path === '/spec-driven-development') return <SpecDrivenDevelopment />
  if (_path === '/living-documentation') return <LivingDocumentation />
  if (_path === '/competitive-analysis-template') return <CompetitiveAnalysisTemplate />
  if (_path === '/go-to-market-strategy-template') return <GoToMarketStrategyTemplate />
  if (_path === '/product-launch-checklist') return <ProductLaunchChecklist />
  if (_path === '/bug-report-template') return <BugReportTemplate />
  if (_path === '/technical-design-document') return <TechnicalDesignDocument />
  if (_path === '/best-ai-tools-for-product-managers') return <BestAiToolsForProductManagers />
  if (_path === '/best-product-management-software') return <BestProductManagementSoftware />
  if (_path === '/best-ai-note-taking-app') return <BestAiNoteTakingApp />
  if (_path === '/product-roadmap-example') return <ProductRoadmapExample />
  if (_path === '/release-plan-template') return <ReleasePlanTemplate />
  if (_path === '/feature-request-template') return <FeatureRequestTemplate />
  if (_path === '/what-is-a-product-roadmap') return <WhatIsAProductRoadmap />
  if (_path === '/prioritization-frameworks') return <PrioritizationFrameworks />
  if (_path === '/product-strategy') return <ProductStrategy />
  if (_path === '/best-knowledge-base-software') return <BestKnowledgeBaseSoftware />
  if (_path === '/content-roadmap') return <ContentRoadmap />
  if (_path === '/product-discovery') return <ProductDiscovery />
  if (_path === '/what-is-product-management') return <WhatIsProductManagement />
  if (_path === '/release-notes') return <ReleaseNotes />
  if (_path === '/user-story-template') return <UserStoryTemplate />
  if (_path === '/acceptance-criteria') return <AcceptanceCriteria />
  if (_path === '/swot-analysis-template') return <SwotAnalysisTemplate />
  if (_path === '/creative-brief-template') return <CreativeBriefTemplate />
  if (_path === '/project-proposal-template') return <ProjectProposalTemplate />
  if (_path === '/business-roadmap') return <BusinessRoadmap />
  if (_path === '/technical-documentation-template') return <TechnicalDocumentationTemplate />
  if (_path === '/from-meeting-notes-to-decisions') return <FromMeetingNotesToDecisions />
  if (_path === '/product-management-metrics') return <ProductManagementMetrics />

  // Multilingual SEO landing pages (Tier-1: JA + DE). Single-locale reframe
  // bridges + PM-concept pages; native content, no EN/ZH sibling. See
  // outreach/seo-multilingual-plan.md.
  if (_path === '/ja/transcription') return <JaTranscription />
  if (_path === '/ja/meeting-minutes-ai') return <JaMeetingMinutes />
  if (_path === '/ja/summary') return <JaSummary />
  if (_path === '/de/audio-transkribieren') return <DeAudioTranskribieren />
  if (_path === '/de/produkt-roadmap') return <DeProduktRoadmap />
  if (_path === '/de/prd-anforderungsdokument') return <DePrd />
  if (_path === '/fr/prd-document-exigences-produit') return <FrPrd />
  if (_path === '/fr/retranscription-entretien') return <FrRetranscriptionEntretien />
  if (_path === '/zh-tw/voice-to-text') return <ZhTwVoiceToText />
  if (_path === '/zh-tw/meeting-minutes-ai') return <ZhTwMeetingMinutes />
  if (_path === '/es/transcribir-entrevista') return <EsTranscribirEntrevista />
  if (_path === '/es/ia-para-tomar-notas-de-reuniones') return <EsNotasReuniones />
  if (_path === '/ko/voice-to-text') return <KoVoiceToText />
  if (_path === '/ko/pdf-summary') return <KoPdfSummary />
  if (_path === '/it/trascrizione-audio') return <ItTrascrizioneAudio />
  if (_path === '/it/riassunto-pdf') return <ItRiassuntoPdf />
  if (_path === '/ja/video-transcription') return <JaVideoTranscription />
  if (_path === '/ja/voice-recorder-transcription') return <JaVoiceRecorderTranscription />
  if (_path === '/ko/youtube-summary') return <KoYoutubeSummary />
  if (_path === '/zh-tw/video-to-text') return <ZhTwVideoToText />
  if (_path === '/zh-tw/cornell-notes-method') return <ZhTwCornellNotes />
  if (_path === '/de/video-transkribieren') return <DeVideoTranskribieren />
  if (_path === '/it/trascrizione-video') return <ItTrascrizioneVideo />
  if (_path === '/fr/transcription-audio-en-texte') return <FrTranscriptionAudio />
  if (_path === '/ja/decision-log') return <JaDecisionLog />
  if (_path === '/zh-tw/decision-log') return <ZhTwDecisionLog />
  if (_path === '/zh-tw/decision-log-template') return <ZhTwDecisionLogTemplate />
  if (_path === '/zh-tw/product-brief-template') return <ZhTwProductBriefTemplate />
  if (_path === '/ko/decision-log') return <KoDecisionLog />
  if (_path === '/es/registro-de-decisiones') return <EsRegistroDecisiones />
  if (_path === '/it/registro-delle-decisioni') return <ItRegistroDecisioni />
  if (_path === '/fr/journal-de-decisions') return <FrJournalDecisions />
  if (_path === '/de/entscheidungsprotokoll') return <DeEntscheidungsprotokoll />
  if (_path === '/ja/comparisons/notion-ai') return <NotionAiJa />
  if (_path === '/zh-tw/comparisons/notion-ai') return <NotionAiZhTw />
  if (_path === '/ko/comparisons/notion-ai') return <NotionAiKo />
  if (_path === '/es/comparisons/notion-ai') return <NotionAiEs />
  if (_path === '/it/comparisons/notion-ai') return <NotionAiIt />
  if (_path === '/fr/comparisons/notion-ai') return <NotionAiFr />
  if (_path === '/de/comparisons/notion-ai') return <NotionAiDe />
  if (_path === '/ko/comparisons/obsidian') return <ObsidianKo />
  if (_path === '/ja/comparisons/obsidian') return <ObsidianJa />
  if (_path === '/de/comparisons/confluence') return <ConfluenceDe />
  if (_path === '/de/comparisons/trello') return <TrelloDe />
  if (_path === '/zh-tw/comparisons/coda') return <CodaZhTw />
  if (_path === '/ja/product-discovery') return <JaProductDiscovery />
  if (_path === '/zh-tw/product-discovery') return <ZhTwProductDiscovery />
  if (_path === '/ko/product-discovery') return <KoProductDiscovery />
  if (_path === '/es/product-discovery') return <EsProductDiscovery />
  if (_path === '/it/product-discovery') return <ItProductDiscovery />
  if (_path === '/fr/recherche-produit') return <FrRechercheProduit />
  if (_path === '/de/produktrecherche') return <DeProduktrecherche />
  if (_path === '/ja/product-roadmap') return <JaProductRoadmap />
  if (_path === '/zh-tw/product-roadmap') return <ZhTwProductRoadmap />
  if (_path === '/ko/product-roadmap') return <KoProductRoadmap />
  if (_path === '/es/roadmap-de-producto') return <EsRoadmapProducto />
  if (_path === '/it/roadmap-di-prodotto') return <ItRoadmapProdotto />
  if (_path === '/fr/roadmap-produit') return <FrRoadmapProduit />
  if (_path === '/ja/prd') return <JaPrd />
  if (_path === '/zh-tw/prd') return <ZhTwPrd />
  if (_path === '/ko/prd') return <KoPrd />
  if (_path === '/es/prd-documento-de-requisitos') return <EsPrd />
  if (_path === '/it/prd') return <ItPrd />
  if (_path === '/fr/documentation-produit') return <FrDocumentationProduit />
  if (_path === '/fr/wiki-entreprise') return <FrWikiEntreprise />
  if (_path === '/fr/backlog-produit') return <FrBacklogProduit />
  if (_path === '/de/produktmanagement') return <DeProduktmanagement />
  if (_path === '/de/produktvision') return <DeProduktvision />

  // /zh/* — Chinese SEO variants. Same component shape, native zh content,
  // hreflang bidirectional with en sibling so Google routes zh queries here.
  if (_path === '/zh/comparisons/notion-ai') return <NotionAiZh />
  if (_path === '/zh/comparisons/chatgpt') return <ChatGptZh />
  if (_path === '/zh/comparisons/chatprd') return <ChatPrdZh />
  if (_path === '/zh/comparisons/linear-docs') return <LinearDocsZh />
  if (_path === '/zh/comparisons/coda') return <CodaZh />
  if (_path === '/zh/comparisons/obsidian') return <ObsidianZh />
  if (_path === '/zh/use-cases/pm-roadmap') return <PmRoadmapZh />
  if (_path === '/zh/use-cases/design-system-tracking') return <DesignSystemTrackingZh />
  if (_path === '/zh/use-cases/analyst-investment-memo') return <AnalystInvestmentMemoZh />
  if (_path === '/zh/security') return <SecurityZh />
  if (_path === '/zh/privacy') return <PrivacyZh />
  if (_path === '/zh/data-export') return <DataExportZh />
  if (_path === '/zh/decision-log') return <DecisionLogZh />
  if (_path === '/zh/decision-log-template') return <DecisionLogTemplateZh />
  if (_path === '/zh/architecture-decision-record') return <ArchitectureDecisionRecordZh />
  if (_path === '/zh/ai-prd') return <AiPrdZh />
  if (_path === '/zh/product-requirements-document') return <ProductRequirementsDocumentZh />
  if (_path === '/zh/prd-template') return <PrdTemplateZh />
  if (_path === '/zh/product-brief-template') return <ProductBriefTemplateZh />
  if (_path === '/zh/prfaq') return <PrfaqZh />
  if (_path === '/zh/product-spec-template') return <ProductSpecTemplateZh />
  if (_path === '/zh/spec-driven-development') return <SpecDrivenDevelopmentZh />
  if (_path === '/zh/living-documentation') return <LivingDocumentationZh />
  if (_path === '/zh/competitive-analysis-template') return <CompetitiveAnalysisTemplateZh />
  if (_path === '/zh/go-to-market-strategy-template') return <GoToMarketStrategyTemplateZh />
  if (_path === '/zh/product-launch-checklist') return <ProductLaunchChecklistZh />
  if (_path === '/zh/bug-report-template') return <BugReportTemplateZh />
  if (_path === '/zh/technical-design-document') return <TechnicalDesignDocumentZh />
  if (_path === '/zh/best-ai-tools-for-product-managers') return <BestAiToolsForProductManagersZh />
  if (_path === '/zh/best-product-management-software') return <BestProductManagementSoftwareZh />
  if (_path === '/zh/best-ai-note-taking-app') return <BestAiNoteTakingAppZh />
  if (_path === '/zh/product-roadmap-example') return <ProductRoadmapExampleZh />
  if (_path === '/zh/release-plan-template') return <ReleasePlanTemplateZh />
  if (_path === '/zh/feature-request-template') return <FeatureRequestTemplateZh />
  if (_path === '/zh/what-is-a-product-roadmap') return <WhatIsAProductRoadmapZh />
  if (_path === '/zh/prioritization-frameworks') return <PrioritizationFrameworksZh />
  if (_path === '/zh/product-strategy') return <ProductStrategyZh />
  if (_path === '/zh/best-knowledge-base-software') return <BestKnowledgeBaseSoftwareZh />
  if (_path === '/zh/content-roadmap') return <ContentRoadmapZh />
  if (_path === '/zh/product-discovery') return <ProductDiscoveryZh />
  if (_path === '/zh/what-is-product-management') return <WhatIsProductManagementZh />
  if (_path === '/zh/release-notes') return <ReleaseNotesZh />
  if (_path === '/zh/user-story-template') return <UserStoryTemplateZh />
  if (_path === '/zh/acceptance-criteria') return <AcceptanceCriteriaZh />
  if (_path === '/zh/swot-analysis-template') return <SwotAnalysisTemplateZh />
  if (_path === '/zh/creative-brief-template') return <CreativeBriefTemplateZh />
  if (_path === '/zh/project-proposal-template') return <ProjectProposalTemplateZh />
  if (_path === '/zh/business-roadmap') return <BusinessRoadmapZh />
  if (_path === '/zh/technical-documentation-template') return <TechnicalDocumentationTemplateZh />
  if (_path === '/zh/from-meeting-notes-to-decisions') return <FromMeetingNotesToDecisionsZh />
  if (_path === '/zh/product-management-metrics') return <ProductManagementMetricsZh />
  // doc/07 PR-31: Stripe checkout entry. CheckoutRedirect handles the
  // auth split — authed user gets sent to Stripe directly, unauthed
  // bounces through /login. Marketing CTAs all land here so the
  // trial-start flow lives in one place.
  if (_path === '/checkout') {
    const knownAuthed =
      authState === 'in' ? true : authState === 'out' ? false : undefined
    return <CheckoutRedirect knownAuthed={knownAuthed} />
  }

  if (authState === 'out') {
    // Marketing homepage at `/`; existing LoginPage at `/login`. Marketing
    // route deep-links (/pricing, /for-pm, /for-developers) handled
    // above; everything else falls through to <Homepage />.
    if (_path === '/login') return <LoginPage />
    return <Homepage />
  }
  // Logged-in user who lands on /login (e.g. typed manually or stale
  // bookmark) — clean the URL back to / so the app shell renders at the
  // canonical path. Doesn't navigate (replaceState), so back-button history
  // stays sane.
  if (typeof window !== 'undefined' && _path === '/login') {
    window.history.replaceState(null, '', '/')
  }

  // PR-26: mobile viewport → focused capture companion. Renders BEFORE
  // the admin-errors view because the admin link is desktop-sidebar-only
  // and would never be reachable from mobile anyway. MobileShell builds
  // its own state (project list, cards, peek state) and shares only the
  // resolved `me` payload with the desktop tree above.
  if (isMobile && me) {
    return (
      <MobileShell
        me={me}
        onLogout={async () => {
          await api.logout()
          // Reload so CoreProvider re-boots: /api/me 401s → the login gate.
          window.location.reload()
        }}
      />
    )
  }

  // Unified admin page (Overview / Usage / Errors / Users). The
  // require_admin server check on each endpoint is the actual auth
  // boundary; the entrypoint link is hidden for non-admin users.
  if (view === 'admin') {
    return <AdminShell onBack={() => setView('main')} />
  }

  // Column layout constants. Sidebar (220) and GapLedger (280) are fixed;
  // discussion is user-resizable; cards takes the leftover space with a
  // 320px floor. SNAP_THRESHOLD: dragging within this many px of the
  // right edge (past cards' min) snaps cards closed. RESIZER_W matches
  // ColumnResizer's hit area.
  const SIDEBAR_W = 220
  const GAP_LEDGER_W = 280
  const CARDS_MIN_W = 320
  const RESIZER_W = 8
  const DISCUSSION_MIN_W = 480
  const DISCUSSION_DEFAULT_W = 760
  const SNAP_THRESHOLD = 80

  const maxDiscussionW = Math.max(
    DISCUSSION_MIN_W,
    windowW - SIDEBAR_W - RESIZER_W - CARDS_MIN_W - GAP_LEDGER_W,
  )
  const renderedDiscussionWidth = cardsCollapsed
    ? Math.max(
        DISCUSSION_MIN_W,
        windowW - SIDEBAR_W - 14 /* show-cards tab width */ - GAP_LEDGER_W,
      )
    : Math.max(DISCUSSION_MIN_W, Math.min(discussionWidth, maxDiscussionW))

  const handleResizerDrag = (clientX: number) => {
    // Past the snap threshold (cursor close to GapLedger's left edge) →
    // collapse cards. Preserve discussionWidth so un-collapse restores
    // the user's prior width instead of reverting to default.
    const snapAt = windowW - GAP_LEDGER_W - SNAP_THRESHOLD
    if (clientX >= snapAt) {
      if (!cardsCollapsed) setCardsCollapsed(true)
      return
    }
    if (cardsCollapsed) setCardsCollapsed(false)
    const target = clientX - SIDEBAR_W - RESIZER_W / 2
    setDiscussionWidth(Math.max(DISCUSSION_MIN_W, Math.min(target, maxDiscussionW)))
  }

  const handleResizerKeyStep = (delta: number) => {
    if (cardsCollapsed) {
      setCardsCollapsed(false)
      return
    }
    setDiscussionWidth((w) =>
      Math.max(DISCUSSION_MIN_W, Math.min(w + delta, maxDiscussionW)),
    )
  }

  const handleResizerReset = () => {
    setCardsCollapsed(false)
    setDiscussionWidth(DISCUSSION_DEFAULT_W)
  }

  return (
    <div className="app-shell" style={{ display: 'flex', height: '100dvh', overflow: 'hidden' }}>
      <PaywallBanner me={me} onOpenBilling={() => onOpenSettings('billing')} />
      {/* doc/12 §3.7: early-user WeChat group entry — the launch-phase
          support/feedback channel. Quiet pill, bottom-right. */}
      <FeedbackFab />
      {/* PR-45: first-run onboarding — shows for a new user whose active
          thread has no cards yet. Self-dismisses + persists to localStorage. */}
      <OnboardingOverlay
        eligible={!!activeThreadId && cards.length === 0}
        onTryPrompt={(text) => onSend(text)}
        onClose={() => {}}
      />
      {paywallInfo ? (
        <PaywallModal info={paywallInfo} onClose={() => setPaywallInfo(null)} />
      ) : null}
      <Sidebar
        projects={projects}
        project={project}
        onSelectProject={onSelectProject}
        onNewProject={onNewProject}
        onImportProject={onImportProject}
        onRenameProject={onRenameProject}
        threads={threads}
        activeThreadId={activeThreadId}
        onSelectThread={onSelectThread}
        onNewThread={onNewThread}
        onArchiveThread={onArchiveThread}
        onRenameThread={onRenameThread}
        onOpenSettings={onOpenSettings}
        me={me}
        // Admin entry-point. Only rendered when the server's
        // `is_admin` flag is true. The require_admin server check
        // on each admin endpoint is the actual auth boundary; this
        // just keeps clutter out of normal users' sidebars.
        onOpenAdmin={me?.is_admin ? () => setView('admin') : null}
        busyByThread={core.busyByThread}
        busyProjects={busyProjects}
        unreadByThread={unreadByThread}
        unreadProjects={unreadProjects}
      />
      <DiscussionPane
        projectId={project?.id ?? null}
        threadId={activeThreadId}
        messages={messages}
        usage={core.activeUsage}
        threadModel={
          (activeThreadId && threads.find((t) => t.id === activeThreadId)?.model) || null
        }
        threadEffort={
          (activeThreadId && threads.find((t) => t.id === activeThreadId)?.effort) || null
        }
        threadFastMode={
          (activeThreadId && threads.find((t) => t.id === activeThreadId)?.fast_mode) || false
        }
        threadHadAttachment={
          (activeThreadId && threads.find((t) => t.id === activeThreadId)?.had_attachment) || false
        }
        modelChoices={modelChoices}
        onChangeModelPreference={async (patch) => {
          if (!activeThreadId) return
          // Optimistic: flip the local row immediately, roll back on
          // failure. setThreadModelPreference returns the canonical
          // post-row, so on success we replace the optimistic guess.
          const tid = activeThreadId
          const before = threads.find((t) => t.id === tid)
          if (!before) return
          const optimistic = {
            ...before,
            ...(patch.model !== undefined ? { model: patch.model } : {}),
            ...(patch.effort !== undefined ? { effort: patch.effort } : {}),
            ...(patch.fast_mode !== undefined ? { fast_mode: patch.fast_mode } : {}),
          }
          core.updateThread(tid, () => optimistic)
          // Remember the user's tier choice so the next new thread
          // inherits it. Only fire when `model` is part of the patch —
          // effort/fast-mode changes don't change tier.
          if (patch.model !== undefined) setPreferredTier(patch.model)
          try {
            const updated = await api.setThreadModelPreference(tid, patch)
            core.updateThread(tid, () => updated)
          } catch (e) {
            core.updateThread(tid, () => before)
            setError(t('app.setModelPreferenceFailed', { error: (e as Error).message }))
          }
        }}
        onSend={onSend}
        busy={busy}
        onStopTurn={busy ? () => core.cancelActiveTurn() : null}
        queuedSend={
          core.activeQueued
            ? {
                text: core.activeQueued.displayText,
                imageRefs: core.activeQueued.imageRefs,
              }
            : null
        }
        onCancelQueuedSend={() => core.cancelQueuedSend()}
        onDistill={onDistill}
        distilling={distilling}
        highlightTurnIds={highlightTurnIds}
        pinnedGap={pinnedGap}
        onResolveGap={() => {
          // Capture the resolved gap id BEFORE unpinning so the
          // post-resolve banner can name it.
          const resolvedId = core.pinnedGap
          core.unpinGap()
          if (resolvedId) setPostResolveAuditFor(resolvedId)
        }}
        postResolveAuditFor={postResolveAuditFor}
        onPostResolveAudit={() => {
          setPostResolveAuditFor(null)
          void onDistill('audit')
        }}
        onDismissPostResolveAudit={() => setPostResolveAuditFor(null)}
        onConvertToGaps={onConvertToGaps}
        convertingToGaps={distilling && distillMode === 'decompose'}
        onApprovePinnedGap={onApproveProposal}
        onRejectPinnedGap={onRejectProposal}
        onApproveProposal={onApproveProposal}
        onApproveProposalBatch={onApproveProposalBatch}
        onRejectProposal={onRejectProposal}
        onDiscussProposal={onDiscussProposal}
        jumpToTurn={turnJumpRequest}
        cards={cards}
        onJumpToCard={onJumpToCard}
        onSelectCard={onSelectCard}
        width={renderedDiscussionWidth}
      />
      <ColumnResizer
        collapsed={cardsCollapsed}
        onDragMove={handleResizerDrag}
        onKeyStep={handleResizerKeyStep}
        onReset={handleResizerReset}
        onUncollapse={() => setCardsCollapsed(false)}
      />
      {!cardsCollapsed && (
        <CardsPanel
          projectId={project?.id ?? null}
          projectName={project?.name ?? null}
          projectPack={project?.pack ?? null}
          cards={cards}
          selectedId={selectedCardId}
          highlightId={highlightCardId}
          hoverHighlightIds={hoverHighlightCardIds}
          onHoverRefs={(refs) => setHoverHighlightCardIds(new Set(refs))}
          onSelect={onSelectCard}
          onPeek={onPeekCard}
          onGapClick={onGapClick}
          onGenerate={onOpenDocuments}
          generating={generating}
          staleDocCount={staleDocIds.size}
          onFrameGapsChange={setFrameGaps}
          onShowHistory={setHistoryModalCardId}
          switching={switching}
          framesRevision={framesRevision}
        />
      )}
      <GapLedger
        cards={cards}
        onSelect={onSelectCard}
        onApproveProposal={onApproveProposal}
        onRejectProposal={onRejectProposal}
        onPeek={onPeekCard}
        onDiscussProposal={onDiscussProposal}
        onAnswerQuestion={onDiscussProposal}
        onArchiveQuestion={onArchiveQuestion}
        onHoverTurns={(turnIds) => {
          // Empty array = mouseleave; replace the set so the highlight clears.
          setHighlightTurnIds(new Set(turnIds))
        }}
        onJumpToTurn={(turnId) => setTurnJumpRequest({ turnId, nonce: Date.now() })}
        frameGaps={frameGaps}
        onFrameGapClick={(g) => setGapProbe({ frameId: g.frameId, node: g.node })}
        onAuditGaps={() => onDistill('audit')}
        canAudit={!!activeThreadId && !busy}
        auditing={distilling}
      />
      {peeked && peekedCard && (
        <CardPeek
          card={peekedCard}
          anchor={peeked.anchor}
          knownIds={knownCardIds}
          initialMode={peeked.mode}
          onClose={onClosePeek}
          onSave={onSavePeek}
          onDelete={onDeletePeek}
          onDirtyChange={setPeekDirty}
          onSaveAndApprove={onSaveAndApprovePeek}
          onJumpToCard={(id) => {
            // Navigating away from a peek via wikilink: dirty-guard the close
            // (same as the X button), then select the target so the panel's
            // CardRow scroll-into-view effect reveals it.
            confirmIfDirty(() => {
              setPeeked(null)
              setPeekDirty(false)
              setSelectedCardId(id)
            })
          }}
          projectId={project?.id}
          onRestored={() => {
            // Restore is recorded as a fresh op=patch on the server, which
            // broadcasts CardUpdated → SSE handler refetches the list. The
            // explicit refetch here is belt-and-braces so the peek's body
            // updates immediately even if SSE is briefly delayed.
            if (project) {
              core.refreshCards()
            }
          }}
          onShowHistory={setHistoryModalCardId}
          onSetImplementationStatus={onSetImplementationStatus}
          onArchive={async (archived) => {
            if (!project || !peekedCard) return
            // Server flips cards.archived AND broadcasts CardUpdated, so
            // every tab's lens recovers via the existing SSE handler. We
            // just need the call — no local setCards here.
            await api.setCardArchived(project.id, peekedCard.card_id, archived)
          }}
        />
      )}

      {/* History modal — opened from broken-ref chips and (eventually)
          anywhere else that wants to surface "what was this card?".
          Restore is enabled: the server route handles both alive cards
          (update_card path) and deleted cards (restore_deleted_card path
          re-creates the .md). */}
      {historyModalCardId && project && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.2)', zIndex: 1599 }}
          onClick={() => setHistoryModalCardId(null)}
        >
          <HistoryDrawer
            projectId={project.id}
            cardId={historyModalCardId}
            onClose={() => setHistoryModalCardId(null)}
            onRestored={() => {
              // Undelete-from-broken-ref or restore-of-an-existing-card both
              // surface here. SSE will refresh the cards list; the close is
              // the polite default since the user just took a final action.
              if (project) {
                core.refreshCards()
              }
              setHistoryModalCardId(null)
            }}
          />
        </div>
      )}

      {gapProbe && (
        <GapProbePopover
          node={gapProbe.node}
          onClose={() => setGapProbe(null)}
          onAskAgent={(text) => {
            setGapProbe(null)
            void onSend(text)
          }}
        />
      )}

      {generated && (
        <GenerateModal
          markdown={generated.markdown}
          template={generated.template}
          cardCount={generated.card_count}
          streaming={generating}
          onSave={onSaveGenerated}
          onClose={() => {
            setGenerated(null)
            setPendingSnapshot(null)
          }}
        />
      )}

      {documentsOpen && project && (
        <DocumentsModal
          projectId={project.id}
          cards={cards}
          onGenerate={runGenerate}
          onClose={() => setDocumentsOpen(false)}
        />
      )}

      <DistillModal
        open={distillOpen}
        itemsSoFar={distillItems}
        cards={distillCards}
        gaps={distillGaps}
        errorMsg={distillError}
        mode={distillMode}
        onChangeMode={(next) => onDistill(next)}
        onClose={() => setDistillOpen(false)}
        onAccept={onDistillAccept}
      />

      {showImportModal && (
        <ImportModal
          onImported={onImportSuccess}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {showSettingsModal && (
        <SettingsModal
          project={project}
          onPackChanged={onPackChanged}
          onProjectDeleted={onProjectDeleted}
          onClose={() => {
            setShowSettingsModal(false)
            // Clear the requested initial tab so the next open derives
            // fresh from its caller's intent (Sidebar's avatar vs
            // SETTINGS row vs MCP chip).
            setSettingsInitialSection(undefined)
          }}
          me={me}
          onLogout={async () => {
            await api.logout()
            // Reload so CoreProvider re-boots: /api/me 401s → the login gate.
            window.location.reload()
          }}
          onOpenAdmin={me?.is_admin ? () => setView('admin') : null}
          initialSection={settingsInitialSection}
        />
      )}

      {pendingConfirm && (
        <ConfirmDialog
          title={pendingConfirm.title}
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          destructive={pendingConfirm.destructive}
          onConfirm={pendingConfirm.onConfirm}
          onCancel={() => setPendingConfirm(null)}
        />
      )}

      {(error || toast || core.toast) && (
        <div
          style={{
            position: 'fixed',
            bottom: 14,
            left: '50%',
            transform: 'translateX(-50%)',
            background: error ? 'var(--red-soft)' : 'var(--paper-1)',
            border: `1px solid ${error ? 'var(--red)' : 'var(--rule-s)'}`,
            padding: '6px 14px',
            borderRadius: 6,
            fontSize: 12,
            color: error ? 'var(--red)' : 'var(--ink-1)',
            zIndex: 100,
            maxWidth: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
          onClick={() => { setError(null); setToast(null); setToastAction(null); core.dismissToast() }}
        >
          <span>{error || toast || core.toast}</span>
          {!error && toastAction && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                toastAction.onClick()
                setToast(null)
                setToastAction(null)
              }}
              className="mono"
              style={{
                fontSize: 11,
                padding: '3px 9px',
                border: '1px solid var(--rule)',
                borderRadius: 3,
                background: 'white',
                color: 'var(--ink-1)',
                fontWeight: 600,
                letterSpacing: '.05em',
                cursor: 'pointer',
              }}
            >
              {toastAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default App
